// External imports
import { InferRequestType, InferResponseType } from "hono";

// Internal imports
import { client } from "@/lib/hono";
import {
  SaveRestaurantRequest,
  RestaurantFilters,
  GooglePlacesRestaurant
} from "../types/restaurants.types";

// API types based on backend routes - using saved endpoints for user favorites
type SaveRestaurantAPI = typeof client.api.saved.$post;
type GetSavedRestaurantsAPI = typeof client.api.saved.$get;
type GetSavedByIdAPI = typeof client.api.saved[":savedId"]["$get"];
type UnsaveRestaurantAPI = typeof client.api.saved[":restaurantId"]["$delete"];
type CheckSavedAPI = typeof client.api.saved.check[":placeId"]["$get"];
type GetSavedCountAPI = typeof client.api.saved.stats.count.$get;

// Request types
export type SaveRestaurantRequestType = InferRequestType<SaveRestaurantAPI>["json"];
export type GetSavedRestaurantsRequestType = InferRequestType<GetSavedRestaurantsAPI>["query"];

// Response types
export type SaveRestaurantResponseType = InferResponseType<SaveRestaurantAPI>;
export type GetSavedRestaurantsResponseType = InferResponseType<GetSavedRestaurantsAPI>;
export type GetSavedByIdResponseType = InferResponseType<GetSavedByIdAPI>;
export type UnsaveRestaurantResponseType = InferResponseType<UnsaveRestaurantAPI>;
export type CheckSavedResponseType = InferResponseType<CheckSavedAPI>;
export type GetSavedCountResponseType = InferResponseType<GetSavedCountAPI>;

/**
 * Restaurant API service
 */
export const restaurantsService = {
  /**
   * Save a restaurant to favorites - creates restaurant if needed, then saves to user's favorites
   */
  async saveRestaurant(data: SaveRestaurantRequest) {
    const response = await client.api.saved.$post({
      json: {
        placeId: data.placeId,
        restaurantData: data,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to save restaurant: ${response.status}`);
    }

    return await response.json();
  },

  /**
   * Get list of saved restaurants for the current user
   */
  async getRestaurants(filters: RestaurantFilters = {}) {
    const searchParams = new URLSearchParams();

    if (filters.page) searchParams.set('page', filters.page.toString());
    if (filters.pageSize) searchParams.set('pageSize', filters.pageSize.toString());
    if (filters.sortBy) searchParams.set('sortBy', filters.sortBy);
    if (filters.sortOrder) searchParams.set('sortOrder', filters.sortOrder);

    const response = await client.api.saved.$get({
      query: Object.fromEntries(searchParams),
    });

    if (!response.ok) {
      throw new Error(`Failed to get saved restaurants: ${response.status}`);
    }

    return await response.json();
  },

  /**
   * Get a single saved restaurant by saved ID
   */
  async getSavedRestaurantById(savedId: string) {
    const response = await client.api.saved[":savedId"].$get({
      param: { savedId },
    });

    if (!response.ok) {
      throw new Error(`Failed to get saved restaurant: ${response.status}`);
    }

    return await response.json();
  },

  /**
   * Remove a restaurant from favorites (unsave)
   */
  async deleteRestaurant(restaurantId: string) {
    const response = await client.api.saved[":restaurantId"].$delete({
      param: { restaurantId },
    });

    if (!response.ok) {
      throw new Error(`Failed to unsave restaurant: ${response.status}`);
    }

    return await response.json();
  },

  /**
   * Check if a restaurant is already saved (by Google Places ID)
   */
  async checkRestaurantSaved(placeId: string) {
    const response = await client.api.saved.check[":placeId"].$get({
      param: { placeId },
    });

    if (!response.ok) {
      throw new Error(`Failed to check saved status: ${response.status}`);
    }

    return await response.json();
  },

  /**
   * Get saved restaurant count for current user
   */
  async getSavedCount() {
    const response = await client.api.saved.stats.count.$get();

    if (!response.ok) {
      throw new Error(`Failed to get saved count: ${response.status}`);
    }

    return await response.json();
  },
};

/**
 * Helper function to transform Google Places data to our save format
 */
export const transformGooglePlacesToSaveFormat = (
  restaurant: GooglePlacesRestaurant
): SaveRestaurantRequest => {
  return {
    placeId: restaurant.place_id,
    name: restaurant.name,
    formattedAddress: restaurant.formatted_address,
    latitude: restaurant.geometry.location.lat,
    longitude: restaurant.geometry.location.lng,
    rating: restaurant.rating,
    priceLevel: restaurant.price_level,
    types: restaurant.types || [],
    photos: restaurant.photos ? restaurant.photos.map(photo => ({
      // Transform Google Photos to storable format
      height: photo.height,
      width: photo.width,
      html_attributions: photo.html_attributions,
    })) : undefined,
    website: restaurant.website,
  };
};